import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import PageLayout from '../components/PageLayout';
import { useToast } from '../context/ToastContext';
import { formatDateTime } from '../utils/formatDate';
import TablePagination from '../components/TablePagination';
import TableSearch from '../components/TableSearch';

const severityBadge = (s) => {
  const map = {
    critical: 'bg-danger',
    high: 'bg-warning text-dark',
    medium: 'bg-info text-dark',
    low: 'bg-secondary',
    info: 'bg-light text-dark border',
  };
  return `badge ${map[s] || 'bg-secondary'}`;
};

const statusBadge = (s) => {
  const map = {
    open: 'bg-danger',
    acknowledged: 'bg-warning text-dark',
    in_progress: 'bg-primary',
    resolved: 'bg-success',
    false_positive: 'bg-secondary',
  };
  return `badge ${map[s] || 'bg-secondary'}`;
};

const slaCountdown = (dueAt) => {
  if (!dueAt) return { label: '—', className: 'text-muted' };
  const diff = new Date(dueAt) - Date.now();
  const hours = Math.round(diff / 3600000);
  if (diff < 0) return { label: `${Math.abs(hours)}h overdue`, className: 'text-danger fw-bold' };
  if (hours < 24) return { label: `${hours}h left`, className: 'text-warning fw-semibold' };
  const days = Math.ceil(hours / 24);
  return { label: `${days}d left`, className: 'text-success' };
};

const ManagementFindings = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', severity: '', source_module: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  const fetchFindings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/findings', {
        params: {
          page: pagination.currentPage,
          limit: 50,
          search: debouncedSearch || undefined,
          ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        },
      });
      if (res.data.success) {
        setFindings(res.data.data);
        setPagination((p) => ({ ...p, ...res.data.pagination }));
      }
    } catch (err) {
      toastError('Failed to load findings');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, filters, debouncedSearch, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPagination((p) => (p.currentPage === 1 ? p : { ...p, currentPage: 1 }));
  }, [debouncedSearch, filters]);

  useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  useEffect(() => {
    api.get('/api/findings/assignees').then((res) => {
      if (res.data.success) setAssignees(res.data.data);
    }).catch(() => {});
  }, []);

  const openDetail = async (finding) => {
    setSelected(finding);
    setNote('');
    try {
      const res = await api.get(`/api/findings/${finding.id}`);
      if (res.data.success) setDetail(res.data.data);
    } catch {
      toastError('Failed to load finding detail');
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
  };

  const handlePatch = async (patch) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await api.patch(`/api/findings/${selected.id}`, { ...patch, note: note || undefined });
      if (res.data.success) {
        toastSuccess('Finding updated');
        closeDetail();
        fetchFindings();
      }
    } catch {
      toastError('Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleRescan = async () => {
    if (!selected) return;
    setRescanning(true);
    try {
      await api.post(`/api/findings/${selected.id}/rescan`);
      toastSuccess('Rescan completed');
      const res = await api.get(`/api/findings/${selected.id}`);
      if (res.data.success) setDetail(res.data.data);
      fetchFindings();
    } catch {
      toastError('Rescan failed');
    } finally {
      setRescanning(false);
    }
  };

  return (
    <PageLayout title="Findings & Remediation">
      <div className="row mb-4">
        <div className="col d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h2 className="h4 mb-1">
              <i className="las la-exclamation-triangle me-2 text-warning"></i>
              Findings & Remediation
            </h2>
            <p className="text-muted mb-0">Track scan issues across all domains</p>
          </div>
          <Link to="/management/dashboard" className="btn btn-outline-secondary btn-sm">
            <i className="las la-arrow-left me-1"></i> Dashboard
          </Link>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-center mb-2">
            <div className="col-md-6">
              <TableSearch
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search title, domain, module, or assignee…"
              />
            </div>
          </div>
          <div className="row g-2">
            {['', 'open', 'acknowledged', 'in_progress', 'resolved'].map((s) => (
              <div className="col-auto" key={s || 'all'}>
                <button
                  type="button"
                  className={`btn btn-sm ${filters.status === s ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => { setFilters((f) => ({ ...f, status: s })); setPagination((p) => ({ ...p, currentPage: 1 })); }}
                >
                  {s ? s.replace('_', ' ') : 'All statuses'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Severity</th>
                <th>Title</th>
                <th>Domain</th>
                <th>Module</th>
                <th>Status</th>
                <th>Assigned</th>
                <th>SLA</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-4"><div className="spinner-border spinner-border-sm" /></td></tr>
              ) : findings.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-4 text-muted">No findings match your filters</td></tr>
              ) : findings.map((f) => {
                const sla = slaCountdown(f.sla_due_at);
                return (
                  <tr key={f.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(f)}>
                    <td><span className={severityBadge(f.severity)}>{f.severity}</span></td>
                    <td className="fw-semibold">{f.title}</td>
                    <td>{f.domain_name}</td>
                    <td><span className="badge bg-light text-dark border">{f.source_module}</span></td>
                    <td><span className={statusBadge(f.status)}>{f.status.replace('_', ' ')}</span></td>
                    <td>{f.assignee_name || '—'}</td>
                    <td className={sla.className}>{sla.label}</td>
                    <td className="text-muted small">{formatDateTime(f.last_seen_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <TablePagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            showingFrom={pagination.totalCount === 0 ? 0 : (pagination.currentPage - 1) * 50 + 1}
            showingTo={Math.min(pagination.currentPage * 50, pagination.totalCount)}
            onPageChange={(next) => setPagination((p) => ({ ...p, currentPage: next }))}
          />
        </div>
      </div>

      {selected && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(15,23,42,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{detail?.title || selected.title}</h5>
                <button type="button" className="btn-close" onClick={closeDetail} />
              </div>
              <div className="modal-body">
                {detail ? (
                  <>
                    <p className="text-muted">{detail.description}</p>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <label className="form-label small text-muted">Status</label>
                        <select className="form-select form-select-sm" defaultValue={detail.status}
                          onChange={(e) => handlePatch({ status: e.target.value })}>
                          {['open', 'acknowledged', 'in_progress', 'resolved', 'false_positive'].map((s) => (
                            <option key={s} value={s}>{s.replace('_', ' ')}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small text-muted">Assign to</label>
                        <select className="form-select form-select-sm" defaultValue={detail.assigned_to || ''}
                          onChange={(e) => handlePatch({ assigned_to: e.target.value || null })}>
                          <option value="">Unassigned</option>
                          {assignees.map((u) => (
                            <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4 d-flex align-items-end">
                        <button type="button" className="btn btn-outline-primary btn-sm w-100" onClick={handleRescan} disabled={rescanning}>
                          {rescanning ? 'Scanning…' : 'Re-scan to verify'}
                        </button>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label small text-muted">Add note</label>
                      <div className="input-group input-group-sm">
                        <input type="text" className="form-control" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Activity note…" />
                        <button type="button" className="btn btn-secondary" disabled={saving || !note.trim()} onClick={() => handlePatch({})}>Log note</button>
                      </div>
                    </div>
                    <details className="mb-3">
                      <summary className="small text-muted" style={{ cursor: 'pointer' }}>Raw scan reference</summary>
                      <pre className="small bg-light p-2 rounded mt-2" style={{ maxHeight: 160, overflow: 'auto' }}>
                        {JSON.stringify(detail.raw_scan_ref, null, 2)}
                      </pre>
                    </details>
                    <h6 className="small text-uppercase text-muted">Activity log</h6>
                    <ul className="list-group list-group-flush">
                      {(detail.activity_log || []).map((a) => (
                        <li key={a.id} className="list-group-item px-0 py-2 small">
                          <strong>{a.action.replace('_', ' ')}</strong>
                          {a.actor_name && <span className="text-muted"> — {a.actor_name}</span>}
                          {a.note && <div className="text-muted">{a.note}</div>}
                          <div className="text-muted">{formatDateTime(a.created_at)}</div>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default ManagementFindings;
